/**
 * EROS API client (docs/BUILD-PLAN.md conventions: /api/v1, JSON, RFC 9457-style errors).
 * Endpoint paths live in ./endpoints.ts so wiring to E1's API is a one-file change. The access token lives in memory only; the refresh token is an
 * httpOnly cookie the browser sends by itself. A 401 triggers one silent
 * refresh, and concurrent callers share it.
 */
export interface ApiErrorShape {
  message: string | string[];
  error?: string;
  statusCode: number;
}

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code, e.g. PERIOD_LOCKED (RFC 9457 extension). */
  readonly code: string | undefined;
  /** Field messages from the 422 validation pipe, ready to show inline. */
  readonly fieldMessages: string[];
  /** Field-level errors: field path → message. */
  readonly fields: Record<string, string>;
  /** The raw error payload, for extra fields such as `warnings` on a 409. */
  readonly body: Record<string, unknown>;

  constructor(status: number, message: string, fieldMessages: string[] = [], code?: string, fields: Record<string, string> = {}, body: Record<string, unknown> = {}) {
    super(message);
    this.body = body;
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldMessages = fieldMessages;
    this.fields = fields;
  }
}

let accessToken: string | null = null;

/**
 * Stable id for this browser/handheld (FR-ACC-03 "one active session per handheld").
 * Sent as X-Device-Key on every call and with the login.
 */
export function deviceKey(): string {
  const KEY = 'eros.deviceKey';
  try {
    let k = localStorage.getItem(KEY);
    if (!k) {
      k = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(KEY, k);
    }
    return k;
  } catch {
    return 'no-storage';
  }
}

/** Headers every request carries (also used by the raw fetches in auth and files). */
export function baseHeaders(): Record<string, string> {
  return { 'x-eros-client': 'web', 'x-device-key': deviceKey() };
}
let onUnauthenticated: (() => void) | null = null;
let refreshing: Promise<boolean> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

async function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: baseHeaders(),
      });
      if (!response.ok) return false;
      const body = (await response.json()) as { accessToken: string };
      accessToken = body.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared once settled, so callers that 401 during the refresh share it
      // rather than each starting another with the same cookie.
      queueMicrotask(() => (refreshing = null));
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the silent refresh, e.g. for the login call itself. */
  anonymous?: boolean;
  /** Client-generated UUID for writes the handheld may retry (scan batches, sales). */
  idempotencyKey?: string;
  /** Polling (dashboard refresh, alerts): tells the API this is not user activity for the idle timer. */
  background?: boolean;
  /** Query string values; undefined/empty values are dropped. */
  query?: Record<string, string | number | boolean | undefined | null | (string | number)[]>;
}

/** Builds "?a=1&b=x" from a plain object, skipping blanks. Arrays repeat the key. */
export function toQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => p.append(k, String(x)));
    else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = (): Promise<Response> =>
    fetch(`/api/v1${path}${toQuery(options.query)}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      signal: options.signal,
      headers: {
        ...baseHeaders(),
        ...(options.background ? { 'x-eros-background': '1' } : {}),
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(options.idempotencyKey ? { 'idempotency-key': options.idempotencyKey } : {}),
        ...(accessToken && !options.anonymous ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

  let response = await send();

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      accessToken = null;
      onUnauthenticated?.();
    }
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? (JSON.parse(text) as unknown) : null;
  } catch {
    payload = { message: response.ok ? 'The server sent an unreadable reply.' : `Request failed (${response.status})` };
  }

  if (!response.ok) {
    const shape = payload as (ApiErrorShape & { code?: string; errors?: { field?: string; message: string }[] }) | null;
    const messages = Array.isArray(shape?.message)
      ? shape.message
      : shape?.message
        ? [shape.message]
        : [`Request failed (${response.status})`];
    const fields: Record<string, string> = {};
    for (const e of shape?.errors ?? []) if (e.field && !fields[e.field]) fields[e.field] = e.message;
    throw new ApiError(response.status, messages[0], messages, shape?.code, fields, (payload ?? {}) as Record<string, unknown>);
  }

  return payload as T;
}

/** True when the request never reached the server (offline, DNS, proxy down). */
export const isNetworkError = (e: unknown): boolean => e instanceof TypeError || (e instanceof ApiError && e.status === 0);

/** A page of rows, the shape every list endpoint returns (BUILD-PLAN pagination). */
export interface Page<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}
